import { supabase } from './supabaseClient';

/** Map DB badge.nombre → UI slug used by BADGES_INFO / MemberCard */
export const BADGE_SLUG_BY_NOMBRE = {
  Abanderado: 'abanderado',
  Embajador: 'embajador',
  Capitán: 'capitan',
  Anfitrión: 'anfitrion',
  'Estrella del Mes': 'estrella',
  'Motor de la Comunidad': 'motor',
  'Más Activo': 'activo',
};

export const AVATAR_BUCKET = 'avatares-perfil';
export const BIO_MAX_LEN = 280;

export function mapMemberBadgeRow(row) {
  const nombre = row.badges?.nombre ?? row.badge_nombre;
  const slug = BADGE_SLUG_BY_NOMBRE[nombre];
  if (!slug) return null;
  return {
    id: slug,
    memberBadgeId: row.id,
    featured: !!row.is_featured,
    expiresAt: row.vence_at,
  };
}

export function attachBadgesToMembers(members, badgeRows) {
  const byMember = new Map();
  for (const row of badgeRows || []) {
    const mapped = mapMemberBadgeRow(row);
    if (!mapped) continue;
    const list = byMember.get(row.member_id) || [];
    list.push(mapped);
    byMember.set(row.member_id, list);
  }
  return (members || []).map((m) => ({
    ...m,
    badges: byMember.get(m.id) || [],
  }));
}

/** Public URL for an avatar storage path (or passthrough if already absolute) */
export function avatarPublicUrl(pathOrUrl) {
  if (!pathOrUrl) return null;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const { data } = supabase.storage.from(AVATAR_BUCKET).getPublicUrl(pathOrUrl);
  return data?.publicUrl || null;
}

/** True when foto_url is a path inside our avatars bucket (safe to delete). */
export function isAvatarStoragePath(pathOrUrl) {
  if (!pathOrUrl || /^https?:\/\//i.test(pathOrUrl)) return false;
  return !pathOrUrl.includes('://');
}

/**
 * Upload under {memberId}/avatar-{timestamp}.{ext}.
 * Returns the storage path (not the public URL).
 */
export async function uploadMemberAvatar(memberId, file) {
  if (!memberId) throw new Error('Falta el id del miembro para subir el avatar');
  if (!file) throw new Error('No hay archivo para subir');

  const ext = (file.name?.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
  const path = `${memberId}/avatar-${Date.now()}-${Math.random().toString(36).slice(2, 6)}.${ext}`;

  const { error } = await supabase.storage.from(AVATAR_BUCKET).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) throw error;
  return path;
}

export async function deleteMemberAvatar(pathOrUrl) {
  if (!isAvatarStoragePath(pathOrUrl)) return;
  const { error } = await supabase.storage.from(AVATAR_BUCKET).remove([pathOrUrl]);
  if (error) throw error;
}

export function digitsOnly(value) {
  return String(value || '').replace(/[^\d]/g, '');
}

/** Basic WhatsApp check: optional empty; otherwise 8–15 digits (E.164-ish). */
export function validateWhatsappNumber(value) {
  const raw = String(value || '').trim();
  if (!raw) return { ok: true, value: '' };
  if (!/^[+\d][\d\s().-]*$/.test(raw)) {
    return { ok: false, error: 'Usá solo números, espacios o + (código de país)' };
  }
  const digits = digitsOnly(raw);
  if (digits.length < 8 || digits.length > 15) {
    return { ok: false, error: 'El WhatsApp debe tener entre 8 y 15 dígitos (con código de país)' };
  }
  return { ok: true, value: raw };
}

/** Empty OK; otherwise require a parseable http(s) URL (auto-prefix https). */
export function normalizeOptionalUrl(value) {
  const raw = String(value || '').trim();
  if (!raw) return { ok: true, value: null };
  const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const u = new URL(withProtocol);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') {
      return { ok: false, error: 'La URL debe empezar con http:// o https://' };
    }
    return { ok: true, value: u.toString() };
  } catch {
    return { ok: false, error: 'URL inválida' };
  }
}

export function buildProfileUpdatePayload(form) {
  return {
    nombre: String(form.nombre || '').trim(),
    rubro: form.rubro?.trim() || null,
    capitulo_id: form.capitulo_id || null,
    whatsapp_number: form.whatsapp_number?.trim() || null,
    whatsapp_visible: !!form.whatsapp_visible,
    bio: form.bio?.trim() || null,
    ofrezco: form.ofrezco?.trim() || null,
    busco: form.busco?.trim() || null,
    instagram_url: form.instagram_url || null,
    linkedin_url: form.linkedin_url || null,
    web_url: form.web_url || null,
    ...(form.foto_url !== undefined ? { foto_url: form.foto_url || null } : {}),
  };
}
